import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../contexts/ApiContext';
import { getHttpErrorMessageForUser, isToastOnlyApiErrorMessage } from '../utils/httpClientErrorMessage';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitChipSx,
  FitCellText,
  fitTableTooltipProps,
} from '../utils/fitTable';
import { accountTagChipSx } from '../utils/accountTagChip';
import { displayCardLast4 } from '../utils/personalCardLast4';
import { cashflowChipSx, householdChartBarOpacity, householdChartRowFill } from '../utils/cashflowColors';
import {
  addYearAmount,
  chartAxisEuroTick,
  chartYearKeys,
  chartYearTotals,
  flattenYearStack,
  householdChartClickName,
  HOUSEHOLD_CHART_BAR_BACKGROUND,
  HOUSEHOLD_CHART_HOVER_CURSOR,
  postedYear,
  rowYearShadeFill,
  householdXAxisFromValues,
  yearLegendFill,
  yearLegendText,
} from '../utils/personalFinancesChart';
import { PersonalFinancesChartTooltip } from '../utils/personalFinancesChartTooltip';
import {
  HOUSEHOLD_CHART_ROW_HEIGHT,
  HOUSEHOLD_CHART_Y_AXIS_WIDTH,
  householdBarCountLabel,
  householdChartYAxisTick,
} from '../utils/personalFinancesChartLabels';
import {
  applyHouseholdUrlLabels,
  householdCategoryFromChartSelection,
  householdChartSelectionFromFilters,
  householdUrlFiltersFromChart,
  householdUrlGroup,
  householdUrlLabelsFromCatalog,
  isHouseholdMultiSelectEvent,
  normalizePersonalMonth,
  personalMonthLabel,
  postedDateMatchesPersonalMonth,
  readPersonalUrlFilters,
  resolveHouseholdLabel,
  toggleHouseholdChartSelection,
  writePersonalUrlFilters,
} from './personalFinancesUrl';
import { HouseholdSimilarTxDialog } from '../components/HouseholdSimilarTxDialog';
import { HouseholdDetailLine as DetailLine } from '../components/HouseholdDetailLine';
import { HouseholdFilterChip } from '../components/HouseholdFilterChip';
import { HouseholdFilterField } from '../components/HouseholdFilterField';
import { HouseholdTxCommentButton } from '../components/HouseholdTxCommentButton';
import { commentCsvValue, commentSearchText, HouseholdTxCommentThread, type PersonalTxComment } from '../components/HouseholdTxCommentThread';
import {
  CHART_EXPENSES,
  CHART_INCOME,
  CHART_INVESTMENTS_PROPERTY,
  CHART_INVESTMENTS_FINANCE,
  CHART_MORTGAGE_LUMP,
  CHART_NA,
  CHART_TRANSFERS,
  CHART_UNKNOWN,
  CASHFLOW_FILTER_OPTIONS,
  DEFAULT_OMHE_CATEGORY_GROUPS,
  allocationOfKind,
  cashflowOfRow,
  categoriesFromApi,
  displayCashflowLabel,
  displayHouseholdCategory,
  expenseGroupOfRow,
  expenseSignedAmount,
  groupInvestmentPropertyChartRows,
  isInvestmentCashflow,
  isNettingCashflow,
  isInvestmentPropertyName,
  groupNameOf,
  householdRowsSimilarTo,
  householdSortHeaderSx,
  householdOffsetChipColor,
  householdOffsetChipVariant,
  matchesHouseholdCategoryFilter,
  rowMatchesHouseholdChartBars,
  openDetailRowSx,
  pairedOffsetIds,
  similarSearchNeedle,
} from '../utils/personalFinancesHousehold';
import {
  buildStatementRanks,
  clockFromIso,
  compareLedgerDateSort,
  formatDayMonthYear,
  formatPostedDateTime,
  type LedgerSortRow,
} from '../utils/personalStatementOrder';

export interface OmheTransaction {
  id: number;
  posted_account: string;
  product: string;
  posted_date: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  description: string;
  amount: number | null;
  debit_amount: number | null;
  credit_amount: number | null;
  fee: number | null;
  balance: number | null;
  posted_currency: string;
  transaction_type: string;
  state: string;
  proposed_category?: string | null;
  expense_kind?: string | null;
  category_rationale?: string | null;
  card_last4?: string | null;
  offset_of_id?: number | null;
  user_comment?: string | null;
  comments?: PersonalTxComment[] | null;
}

type HealthPayoutSuggestion = {
  id: number;
  posted_date: string | null;
  description: string;
  debit_amount: number;
  posted_account: string;
  proposed_category: string | null;
  days_before: number;
  ratio: number;
  preferred?: boolean;
};

const isHealthInsurancePayout = (row: { proposed_category?: string | null } | null | undefined) =>
  displayHouseholdCategory(row?.proposed_category) === 'Health Insurance Payout';

interface OmheStats {
  total_transactions: number;
  min_date: string | null;
  max_date: string | null;
  total_debit: number;
  total_credit: number;
  latest_balance: number | null;
  transaction_types: string[];
  type_totals?: Array<{
    transaction_type: string;
    count: number;
    debit: number;
    credit: number;
  }>;
  account_totals?: Array<{
    account: string;
    ending: string;
    count: number;
    debit: number;
    credit: number;
  }>;
  years?: number[];
}

type PageSize = 10 | 25 | 50 | 100 | 200 | 1000;
const PAGE_SIZE_OPTIONS: PageSize[] = [10, 25, 50, 100, 200, 1000];
const CURRENT_YEAR = String(new Date().getFullYear());
const OMHE_TABLE_COLS = buildFitColgroup([
  { key: 'select', pct: 3 },
  { key: 'id', pct: 4 },
  { key: 'date', pct: 6 },
  { key: 'cashflow', pct: 5 },
  { key: 'group', pct: 7 },
  { key: 'category', pct: 10 },
  { key: 'allocation', pct: 6 },
  { key: 'type', pct: 4 },
  { key: 'product', pct: 3 },
  { key: 'description', pct: 19 },
  { key: 'debit', pct: 5 },
  { key: 'credit', pct: 5 },
  { key: 'fee', pct: 3 },
  { key: 'balance', pct: 5 },
  { key: 'card', pct: 3 },
  { key: 'account', pct: 4 },
  { key: 'actions', pct: 8 },
]);

const DETAIL_PANEL_WIDTH_PX = 440;

const cashflowColor = (side: string): 'error' | 'info' | 'default' => {
  if (side === CHART_EXPENSES) return 'error';
  if (side === CHART_TRANSFERS) return 'info';
  return 'default';
};

const money = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const optionLabel = (name: string, debit?: number, credit?: number, count?: number) => {
  const amount = Math.round(Math.abs(Number(debit || 0)) + Math.abs(Number(credit || 0)));
  return `${name} (${count || 0}, €${amount.toLocaleString('en-IE')})`;
};

type OmheSortKey =
  | 'id'
  | 'posted_date'
  | 'cashflow'
  | 'category_group'
  | 'proposed_category'
  | 'allocation'
  | 'transaction_type'
  | 'product'
  | 'description'
  | 'debit_amount'
  | 'credit_amount'
  | 'fee'
  | 'balance'
  | 'card_last4'
  | 'posted_account'
  | 'state';

const omheClock = (row: Pick<OmheTransaction, 'completed_at' | 'started_at'>) =>
  clockFromIso(row.completed_at) || clockFromIso(row.started_at);

const omheLedgerRow = (row: OmheTransaction): LedgerSortRow => ({
  id: row.id,
  posted_account: row.posted_account,
  product: row.product,
  posted_date: row.posted_date,
  debit_amount: row.debit_amount,
  credit_amount: row.credit_amount,
  fee: row.fee,
  balance: row.balance,
  clock: omheClock(row),
});

const formatOmheWhen = (row: OmheTransaction) =>
  formatPostedDateTime(row.posted_date, omheClock(row));

const OmheOffsetSideCard: React.FC<{
  row: OmheTransaction;
  role: 'this' | 'other';
  offsetLabel: string;
  onOpen?: () => void;
}> = ({ row, role, offsetLabel, onOpen }) => {
  const heading = role === 'this' ? 'This side' : 'Other side';
  const amount = row.debit_amount
    ? `Debit ${money(row.debit_amount)}`
    : row.credit_amount
      ? `Credit ${money(row.credit_amount)}`
      : '—';
  return (
    <Paper
      variant="outlined"
      data-testid={`omhe-detail-side-${role}`}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `View TX ${row.id}` : undefined}
      sx={{
        p: 1.5,
        mb: 1.5,
        cursor: onOpen ? 'pointer' : 'default',
        borderColor: role === 'this' ? 'primary.light' : 'divider',
        bgcolor: role === 'this' ? 'action.hover' : 'background.paper',
        '&:hover': onOpen ? { borderColor: 'primary.main' } : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="subtitle2">
          {heading} · TX {row.id}
        </Typography>
        {offsetLabel ? (
          <Chip
            size="small"
            label={offsetLabel}
            color={householdOffsetChipColor(offsetLabel)}
            variant={householdOffsetChipVariant(offsetLabel)}
          />
        ) : null}
      </Stack>
      <Typography variant="body2">
        {formatOmheWhen(row)} · {row.posted_account || '—'} · {amount}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {displayHouseholdCategory(row.proposed_category)} · {row.description || '—'}
      </Typography>
      {onOpen ? (
        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.75 }}>
          View this row
        </Typography>
      ) : null}
    </Paper>
  );
};

const omheSortValue = (
  row: OmheTransaction,
  sortBy: OmheSortKey,
  groups: Record<string, string>,
  pairedIds: Set<number>
): string | number | null => {
  switch (sortBy) {
    case 'cashflow':
      return cashflowOfRow(row, groups, pairedIds, true);
    case 'category_group':
      return expenseGroupOfRow(row, groups, pairedIds, true);
    case 'proposed_category':
      return displayHouseholdCategory(row.proposed_category);
    case 'allocation':
      return allocationOfKind(row.expense_kind);
    default: {
      const value = row[sortBy as keyof OmheTransaction];
      if (typeof value === 'string' || typeof value === 'number') return value;
      return value == null ? null : String(value);
    }
  }
};

const compareOmheRows = (
  left: OmheTransaction,
  right: OmheTransaction,
  sortBy: OmheSortKey,
  sortDir: 'asc' | 'desc',
  ranks: Map<number, number>,
  groups: Record<string, string>,
  pairedIds: Set<number>
) => {
  if (sortBy === 'posted_date') {
    return compareLedgerDateSort(omheLedgerRow(left), omheLedgerRow(right), sortDir, ranks);
  }
  const dir = sortDir === 'asc' ? 1 : -1;
  const a = omheSortValue(left, sortBy, groups, pairedIds);
  const b = omheSortValue(right, sortBy, groups, pairedIds);
  if (a == null && b == null) return (left.id - right.id) * dir;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
  return String(a).localeCompare(String(b), undefined, { numeric: true }) * dir || (left.id - right.id) * dir;
};

const OmheSortHeader: React.FC<{
  column: OmheSortKey;
  label: string;
  sortBy: OmheSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (column: OmheSortKey) => void;
}> = ({ column, label, sortBy, sortDir, onSort }) => (
  <TableCell
    align="center"
    sortDirection={sortBy === column ? sortDir : false}
    sx={householdSortHeaderSx}
  >
    <TableSortLabel
      active={sortBy === column}
      direction={sortBy === column ? sortDir : 'asc'}
      onClick={() => onSort(column)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

export const buildOmheTableCsv = (rows: OmheTransaction[]) => {
  const pairedIds = pairedOffsetIds(rows);
  const header = [
    'ID',
    'Date',
    'Cashflow',
    'Group',
    'Category',
    'Allocation',
    'Type',
    'Product',
    'Description',
    'Debit',
    'Credit',
    'Fee',
    'Balance',
    'Card',
    'Account',
    'State',
    'Comment',
  ];
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const amount = (value: number | null) => (value == null ? '' : Number(value).toFixed(2));
  const lines = [
    header.join(','),
    ...rows.map((row) =>
      [
        String(row.id),
        formatOmheWhen(row),
        cashflowOfRow(row, DEFAULT_OMHE_CATEGORY_GROUPS, pairedIds, true),
        expenseGroupOfRow(row, DEFAULT_OMHE_CATEGORY_GROUPS, pairedIds, true),
        displayHouseholdCategory(row.proposed_category),
        allocationOfKind(row.expense_kind),
        row.transaction_type || '',
        row.product || '',
        row.description || '',
        amount(row.debit_amount),
        amount(row.credit_amount),
        amount(row.fee),
        amount(row.balance),
        row.card_last4 || '',
        row.posted_account || '',
        row.state || '',
        commentCsvValue(row),
      ]
        .map(escape)
        .join(',')
    ),
  ];
  return `${lines.join('\n')}\n`;
};

const PaginationBar: React.FC<{
  total: number;
  page: number;
  pages: number;
  perPage: PageSize;
  onPage: (page: number) => void;
  onPerPage: (size: PageSize) => void;
  testId: string;
  search?: React.ReactNode;
}> = ({ total, page, pages, perPage, onPage, onPerPage, testId, search }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    flexWrap="wrap"
    useFlexGap
    data-testid={testId}
    sx={{ width: 1 }}
  >
    <Typography variant="body2">{total} rows</Typography>
    <FormControl size="small" sx={{ minWidth: 96 }}>
      <InputLabel>Rows</InputLabel>
      <Select
        label="Rows"
        value={String(perPage)}
        onChange={(event) => onPerPage(Number(event.target.value) as PageSize)}
      >
        {PAGE_SIZE_OPTIONS.map((size) => (
          <MenuItem key={String(size)} value={String(size)}>
            {size}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <Pagination
      count={pages}
      page={page}
      onChange={(_, next) => onPage(next)}
      size="small"
      disabled={pages <= 1}
    />
    {search ? <Box sx={{ ml: 'auto' }}>{search}</Box> : null}
  </Stack>
);

interface Props {
  year: string;
  onYearChange: (year: string) => void;
  onRowsChange: (rows: OmheTransaction[]) => void;
  reloadToken: number;
}

export const PersonalFinancesOmhe: React.FC<Props> = ({ year, onYearChange, onRowsChange, reloadToken }) => {
  const initialLabels = householdUrlLabelsFromCatalog(DEFAULT_OMHE_CATEGORY_GROUPS);
  const initialUrl = applyHouseholdUrlLabels(readPersonalUrlFilters(), initialLabels);
  const initialSelection = householdChartSelectionFromFilters(initialUrl, initialLabels);
  const [allRows, setAllRows] = useState<OmheTransaction[]>([]);
  const [stats, setStats] = useState<OmheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(initialUrl.q);
  const [search, setSearch] = useState(initialUrl.q);
  const [account, setAccount] = useState(initialUrl.account);
  const [month, setMonth] = useState(normalizePersonalMonth(initialUrl.month, initialUrl.year));
  const [transactionType, setTransactionType] = useState(initialUrl.type);
  const [chartOpen, setChartOpen] = useState(true);
  const [chartSide, setChartSide] = useState(initialSelection.side);
  const [chartGroup, setChartGroup] = useState(initialSelection.group);
  const [category, setCategory] = useState(householdCategoryFromChartSelection(initialSelection));
  const [selectedChartNames, setSelectedChartNames] = useState<string[]>(initialSelection.selected);
  const [categoryGroups, setCategoryGroups] = useState<Record<string, string>>(DEFAULT_OMHE_CATEGORY_GROUPS);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkCategory, setBulkCategory] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<PageSize>(200);
  const [sortBy, setSortBy] = useState<OmheSortKey>('posted_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [detailRowId, setDetailRowId] = useState<number | null>(null);
  const [payoutSuggestions, setPayoutSuggestions] = useState<HealthPayoutSuggestion[]>([]);
  const [payoutSuggestionsLoading, setPayoutSuggestionsLoading] = useState(false);
  const [payoutSuggestionBusyId, setPayoutSuggestionBusyId] = useState<number | null>(null);
  const [commentFocus, setCommentFocus] = useState(false);
  const [similarRowId, setSimilarRowId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const loadSeq = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const urlChart = householdUrlFiltersFromChart({
      side: chartSide,
      group: chartGroup,
      selected: selectedChartNames,
      catalog: categoryGroups,
    });
    writePersonalUrlFilters({
      ...readPersonalUrlFilters(),
      tab: 'omhe',
      year,
      month,
      q: search,
      account,
      type: transactionType,
      side: urlChart.side,
      group: urlChart.group,
      category: urlChart.category,
    });
  }, [year, month, search, account, transactionType, chartSide, chartGroup, selectedChartNames, categoryGroups]);

  useEffect(() => {
    const applyUrl = () => {
      const labels = householdUrlLabelsFromCatalog(categoryGroups);
      const next = applyHouseholdUrlLabels(readPersonalUrlFilters(), labels);
      const selection = householdChartSelectionFromFilters(next, labels);
      setSearchInput(next.q);
      setSearch(next.q);
      setAccount(next.account);
      setMonth(normalizePersonalMonth(next.month, next.year));
      setTransactionType(next.type);
      setChartSide(selection.side);
      setChartGroup(selection.group);
      setSelectedChartNames(selection.selected);
      setCategory(householdCategoryFromChartSelection(selection));
      setPage(1);
    };
    window.addEventListener('popstate', applyUrl);
    return () => window.removeEventListener('popstate', applyUrl);
  }, []);

  const load = useCallback(async () => {
    const seq = (loadSeq.current += 1);
    setLoading(true);
    setAllRows([]);
    setError('');
    try {
      const params = new URLSearchParams({ page: '1', per_page: 'all' });
      if (account) params.set('account', account);
      if (year) params.set('year', year);
      const statsParams = new URLSearchParams();
      if (account) statsParams.set('account', account);
      if (year) statsParams.set('year', year);
      const statsQuery = statsParams.toString();
      const loadCfg = { timeout: 180000 };
      const [listRes, statsRes, catRes] = await Promise.all([
        api.get(`/api/personal-finances/omhe?${params.toString()}`, loadCfg),
        api.get(
          statsQuery ? `/api/personal-finances/omhe/stats?${statsQuery}` : '/api/personal-finances/omhe/stats',
          loadCfg
        ),
        api.get('/api/personal-finances/omhe/categories', loadCfg).catch(() => ({ data: { categories: [] } })),
      ]);
      if (seq !== loadSeq.current) return;
      const rows = listRes.data.transactions || [];
      setAllRows(rows);
      setStats(statsRes.data);
      setCategoryGroups(categoriesFromApi(catRes.data, DEFAULT_OMHE_CATEGORY_GROUPS));
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      setError(getHttpErrorMessageForUser(err, 'Failed to load OmHe personal finances'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [account, year, reloadToken]);

  useEffect(() => {
    if (!month) return;
    if (!year || !month.startsWith(`${year}-`)) setMonth('');
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const labels = householdUrlLabelsFromCatalog(categoryGroups);
    setChartSide((prev) => resolveHouseholdLabel(prev, labels.cashflow));
    setCategory((prev) => resolveHouseholdLabel(prev, labels.categories));
    setChartGroup((prev) =>
      householdUrlGroup(
        resolveHouseholdLabel(prev, labels.groups),
        resolveHouseholdLabel(category, labels.categories),
        categoryGroups,
        resolveHouseholdLabel(chartSide, labels.cashflow)
      )
    );
  }, [category, categoryGroups, chartSide]);

  const statementRanks = useMemo(
    () => buildStatementRanks(allRows.map(omheLedgerRow)),
    [allRows]
  );
  const offsetTargetIds = useMemo(() => {
    const ids = new Set<number>();
    allRows.forEach((row) => {
      if (row.offset_of_id) ids.add(row.offset_of_id);
    });
    return ids;
  }, [allRows]);
  const omhePairedIds = useMemo(() => pairedOffsetIds(allRows), [allRows]);
  const offsetLinkerByTargetId = useMemo(() => {
    const map = new Map<number, OmheTransaction>();
    allRows.forEach((row) => {
      if (row.offset_of_id) map.set(row.offset_of_id, row);
    });
    return map;
  }, [allRows]);
  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const next = allRows.filter((row) => {
      if (transactionType && row.transaction_type !== transactionType) return false;
      if (!postedDateMatchesPersonalMonth(row.posted_date, month)) return false;
      const cat = displayHouseholdCategory(row.proposed_category);
      const side = cashflowOfRow(row, categoryGroups, omhePairedIds, true);
      if (selectedChartNames.length) {
        if (
          !rowMatchesHouseholdChartBars({
            selected: selectedChartNames,
            chartSide,
            chartGroup,
            cashflow: side,
            group: expenseGroupOfRow(row, categoryGroups, omhePairedIds, true),
            category: cat,
            groups: categoryGroups,
          })
        )
          return false;
      } else if (category) {
        if (!matchesHouseholdCategoryFilter(cat, category, categoryGroups)) return false;
        if (chartSide && side !== chartSide) return false;
      } else if (chartSide === CHART_EXPENSES && chartGroup) {
        if (expenseGroupOfRow(row, categoryGroups, omhePairedIds, true) !== chartGroup || side !== CHART_EXPENSES) return false;
      } else if (chartSide && side !== chartSide) return false;
      if (needle) {
        const hay = `${row.id} ${row.offset_of_id || ''} ${offsetLinkerByTargetId.get(row.id)?.id || ''} ${row.description} ${row.posted_account} ${row.product} ${row.transaction_type} ${row.card_last4 || ''} ${cat} ${commentSearchText(row)}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    next.sort((left, right) =>
      compareOmheRows(left, right, sortBy, sortDir, statementRanks, categoryGroups, omhePairedIds)
    );
    return next;
  }, [allRows, search, transactionType, month, chartSide, chartGroup, category, selectedChartNames, categoryGroups, sortBy, sortDir, statementRanks, offsetLinkerByTargetId, omhePairedIds]);

  useEffect(() => {
    onRowsChange(filteredRows);
  }, [filteredRows, onRowsChange]);

  const total = filteredRows.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(page, pages);
  const visibleRows = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, perPage, currentPage]);
  const offsetChip = (row: OmheTransaction) => {
    const other = offsetCounterpart(row);
    const transferLink = Boolean(
      other &&
        (groupNameOf(row.proposed_category, categoryGroups) === CHART_TRANSFERS ||
          groupNameOf(other.proposed_category, categoryGroups) === CHART_TRANSFERS)
    );
    if (row.offset_of_id) {
      if (isHealthInsurancePayout(row)) return `Claim ${row.offset_of_id}`;
      return transferLink ? `Linked ${row.offset_of_id}` : `Offsets ${row.offset_of_id}`;
    }
    if (offsetTargetIds.has(row.id)) {
      if (isHealthInsurancePayout(other)) return 'Reimbursed';
      return transferLink ? 'Linked' : 'Refunded';
    }
    return '';
  };
  const offsetCounterpart = (row: OmheTransaction) =>
    row.offset_of_id
      ? allRows.find((item) => item.id === row.offset_of_id) || null
      : offsetLinkerByTargetId.get(row.id) || null;
  const detailRow = useMemo(
    () => (detailRowId == null ? null : allRows.find((row) => row.id === detailRowId) || null),
    [allRows, detailRowId]
  );
  const similarAnchor = useMemo(
    () => (similarRowId == null ? null : allRows.find((row) => row.id === similarRowId) || null),
    [allRows, similarRowId]
  );
  const similarTxViews = useMemo(() => {
    if (!similarAnchor) return [];
    return householdRowsSimilarTo(allRows, similarAnchor, (row) => row.description).map((row) => ({
      id: row.id,
      when: formatOmheWhen(row),
      description: row.description,
      debit: row.debit_amount != null ? money(row.debit_amount) : '—',
      credit: row.credit_amount != null ? money(row.credit_amount) : '—',
      category: displayHouseholdCategory(row.proposed_category),
      isAnchor: row.id === similarAnchor.id,
    }));
  }, [allRows, similarAnchor]);
  const detailCounterpart = detailRow ? offsetCounterpart(detailRow) : null;
  const openDetailRow = (id: number, options?: { focusComment?: boolean }) => {
    setCommentFocus(Boolean(options?.focusComment));
    setDetailRowId(id);
    setHighlightId(id);
    const index = filteredRows.findIndex((row) => row.id === id);
    if (index >= 0) setPage(Math.floor(index / perPage) + 1);
  };
  const closeDetailRow = () => {
    setCommentFocus(false);
    setDetailRowId(null);
  };
  const openOffsetRow = (id: number) => {
    openDetailRow(id);
  };
  useEffect(() => {
    if (highlightId == null && detailRowId == null) return;
    const targetId = detailRowId ?? highlightId;
    const el = document.querySelector(`[data-testid="omhe-row-${targetId}"]`);
    if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightId, detailRowId, currentPage]);

  const chartRows = useMemo(() => {
    if (chartSide === CHART_INVESTMENTS_PROPERTY) {
      return groupInvestmentPropertyChartRows(
        allRows.filter(
          (row) => cashflowOfRow(row, categoryGroups, omhePairedIds, true) === CHART_INVESTMENTS_PROPERTY
        )
      );
    }
    type Agg = {
      value: number;
      count: number;
      byYear: Record<string, number>;
      countByYear: Record<string, number>;
      incomeExpenseSplit?: boolean;
      credit: number;
      debit: number;
      creditCount: number;
      debitCount: number;
      creditByYear: Record<string, number>;
      debitByYear: Record<string, number>;
      creditCountByYear: Record<string, number>;
      debitCountByYear: Record<string, number>;
    };
    const empty = (): Agg => ({
      value: 0,
      count: 0,
      byYear: {},
      countByYear: {},
      credit: 0,
      debit: 0,
      creditCount: 0,
      debitCount: 0,
      creditByYear: {},
      debitByYear: {},
      creditCountByYear: {},
      debitCountByYear: {},
    });
    const addRow = (agg: Agg, amount: number, year: string) => {
      agg.value += amount;
      agg.count += 1;
      addYearAmount(agg.byYear, year, amount);
      addYearAmount(agg.countByYear, year, 1);
    };
    const amountOf = (row: OmheTransaction, side: string) => {
      if (side === CHART_INCOME) return Number(row.credit_amount || 0);
      if (side === CHART_EXPENSES) return expenseSignedAmount(row);
      if (side === CHART_MORTGAGE_LUMP) {
        return Number(row.credit_amount || 0) - Number(row.debit_amount || 0);
      }
      return Number(row.debit_amount || 0) + Number(row.credit_amount || 0);
    };
    const buckets = new Map<string, Agg>();
    allRows.forEach((row) => {
      if (!postedDateMatchesPersonalMonth(row.posted_date, month)) return;
      const cat = displayHouseholdCategory(row.proposed_category);
      const side = cashflowOfRow(row, categoryGroups, omhePairedIds, true);
      const year = postedYear(row.posted_date);
      let key = side;
      if (chartSide === CHART_EXPENSES && !chartGroup) {
        if (side !== CHART_EXPENSES) return;
        key = expenseGroupOfRow(row, categoryGroups, omhePairedIds, true) || groupNameOf(cat, categoryGroups);
      } else if (chartSide === CHART_EXPENSES && chartGroup) {
        if (
          side !== CHART_EXPENSES ||
          (expenseGroupOfRow(row, categoryGroups, omhePairedIds, true) || groupNameOf(cat, categoryGroups)) !== chartGroup
        )
          return;
        key = cat;
      } else if (chartSide && chartSide !== CHART_EXPENSES) {
        if (side !== chartSide) return;
        key = cat;
      } else if (!chartSide) {
        key = side;
      } else {
        return;
      }
      const amount = amountOf(row, side);
      if (
        amount <= 0 &&
        side !== CHART_EXPENSES &&
        side !== CHART_TRANSFERS &&
        !isInvestmentCashflow(side) &&
        !isNettingCashflow(side) &&
        side !== CHART_UNKNOWN &&
        side !== CHART_NA
      )
        return;
      const current = buckets.get(key) || empty();
      addRow(
        current,
        side === CHART_EXPENSES || side === CHART_MORTGAGE_LUMP ? amount : Math.abs(amount),
        year
      );
      if ((!chartSide && isNettingCashflow(side)) || side === CHART_MORTGAGE_LUMP) {
        const credit = Number(row.credit_amount || 0);
        const debitAmt = Number(row.debit_amount || 0);
        current.incomeExpenseSplit = true;
        current.credit += credit;
        current.debit += debitAmt;
        if (credit > 0) current.creditCount += 1;
        if (debitAmt > 0) current.debitCount += 1;
        addYearAmount(current.creditByYear, year, credit);
        addYearAmount(current.debitByYear, year, debitAmt);
        addYearAmount(current.creditCountByYear, year, credit > 0 ? 1 : 0);
        addYearAmount(current.debitCountByYear, year, debitAmt > 0 ? 1 : 0);
      }
      buckets.set(key, current);
    });
    const order = !chartSide
      ? [CHART_INCOME, CHART_EXPENSES, CHART_TRANSFERS, CHART_INVESTMENTS_PROPERTY, CHART_INVESTMENTS_FINANCE, CHART_MORTGAGE_LUMP, CHART_UNKNOWN]
      : null;
    const names = order || Array.from(buckets.keys()).sort((a, b) => (buckets.get(b)?.value || 0) - (buckets.get(a)?.value || 0));
    return names.map((name) => ({ name, ...(buckets.get(name) || empty()) }));
  }, [allRows, categoryGroups, chartSide, chartGroup, category, month, omhePairedIds]);
  const chartYearStackKeys = useMemo(
    () => chartYearKeys(allRows.map((row) => row.posted_date)),
    [allRows]
  );
  const stackChartByYear =
    !year && chartYearStackKeys.length > 0 && !isNettingCashflow(chartSide);
  const chartPlotData = useMemo(
    () =>
      stackChartByYear
        ? chartRows.map((row) => flattenYearStack(row, chartYearStackKeys))
        : chartRows,
    [chartRows, chartYearStackKeys, stackChartByYear]
  );
  const chartYearAmountTotals = useMemo(
    () => chartYearTotals(chartRows, chartYearStackKeys),
    [chartRows, chartYearStackKeys]
  );
  const chartXAxis = useMemo(
    () => householdXAxisFromValues(chartRows.map((row) => row.value)),
    [chartRows]
  );

  const yearOptions = useMemo(() => {
    const values = new Set<number>();
    (stats?.years || []).forEach((value) => {
      if (value) values.add(Number(value));
    });
    values.add(Number(CURRENT_YEAR));
    return Array.from(values).sort((a, b) => b - a);
  }, [stats]);

  const handleSort = (column: OmheSortKey) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(column === 'posted_date' || column === 'id' ? 'desc' : 'asc');
    }
  };

  const handleChartClick = (name: string, event?: { metaKey?: boolean; ctrlKey?: boolean }) => {
    if (!name) return;
    setPage(1);
    if (isHouseholdMultiSelectEvent(event)) {
      const next = toggleHouseholdChartSelection(selectedChartNames, name);
      setSelectedChartNames(next);
      setCategory(
        householdCategoryFromChartSelection({
          side: chartSide,
          group: chartGroup,
          selected: next,
        })
      );
      return;
    }
    setSelectedChartNames([]);
    if (!chartSide) {
      setChartSide(name);
      setChartGroup('');
      setCategory('');
      return;
    }
    if (chartSide === CHART_INVESTMENTS_PROPERTY) {
      setCategory(name);
      setSelectedChartNames([name]);
      return;
    }
    if (chartSide === CHART_EXPENSES && !chartGroup) {
      setChartGroup(name);
      setCategory('');
      return;
    }
    const nextCat = displayHouseholdCategory(name);
    setCategory(nextCat);
    setSelectedChartNames([nextCat]);
    setChartGroup(householdUrlGroup(chartGroup, nextCat, categoryGroups, chartSide));
  };

  const chartBackParent = selectedChartNames.length
    ? chartGroup || chartSide || 'Income vs expenses'
    : category
      ? chartGroup || chartSide
      : chartGroup
        ? chartSide
        : 'Income vs expenses';

  const stepChartBack = () => {
    setPage(1);
    if (selectedChartNames.length || category) {
      setCategory('');
      setSelectedChartNames([]);
      if (chartSide !== CHART_EXPENSES) setChartGroup('');
      return;
    }
    if (chartGroup) {
      setChartGroup('');
      setSelectedChartNames([]);
      return;
    }
    setChartSide('');
    setSelectedChartNames([]);
  };

  const categoryEditOptions = Object.keys(categoryGroups).filter((name) => name !== CHART_UNKNOWN);
  const visibleIds = visibleRows.map((row) => row.id);
  const selectedOnPage = visibleIds.filter((id) => selectedIds.includes(id));
  const allVisibleSelected = visibleIds.length > 0 && selectedOnPage.length === visibleIds.length;
  const someVisibleSelected = selectedOnPage.length > 0 && !allVisibleSelected;

  const toggleRowSelected = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !visibleIds.includes(id));
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const applyOmheTransaction = (updated: OmheTransaction) => {
    setAllRows((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
  };

  const saveCategory = async (rowId: number, nextCategory: string) => {
    const current = allRows.find((row) => row.id === rowId);
    if (current && displayHouseholdCategory(current.proposed_category) === nextCategory) {
      setEditingId(null);
      return;
    }
    setSavingId(rowId);
    setError('');
    try {
      const response = await api.patch(`/api/personal-finances/omhe/${rowId}`, {
        proposed_category: nextCategory,
      });
      const updated = response.data.transaction as OmheTransaction;
      setAllRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...updated } : row)));
      setEditingId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update category');
    } finally {
      setSavingId(null);
    }
  };

  useEffect(() => {
    if (!detailRow || detailRow.offset_of_id || !isHealthInsurancePayout(detailRow)) {
      setPayoutSuggestions([]);
      setPayoutSuggestionsLoading(false);
      return;
    }
    let cancelled = false;
    setPayoutSuggestionsLoading(true);
    api
      .get(`/api/personal-finances/omhe/${detailRow.id}/offset-suggestions`)
      .then((response) => {
        if (!cancelled) setPayoutSuggestions(response.data?.suggestions || []);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setPayoutSuggestions([]);
          setError(err.response?.data?.error || 'Failed to load suggested payments');
        }
      })
      .finally(() => {
        if (!cancelled) setPayoutSuggestionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailRow]);

  const confirmPayoutSuggestion = async (paymentId: number) => {
    if (!detailRow) return;
    setPayoutSuggestionBusyId(paymentId);
    setError('');
    try {
      const response = await api.post(`/api/personal-finances/omhe/${detailRow.id}/offset`, {
        offset_of_id: paymentId,
      });
      const updated = response.data.transaction as OmheTransaction;
      setAllRows((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
      setPayoutSuggestions([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to confirm suggested payment');
    } finally {
      setPayoutSuggestionBusyId(null);
    }
  };

  const rejectPayoutSuggestion = async (paymentId: number) => {
    if (!detailRow) return;
    setPayoutSuggestionBusyId(paymentId);
    setError('');
    try {
      const response = await api.post(
        `/api/personal-finances/omhe/${detailRow.id}/offset-suggestions/reject`,
        { id: paymentId }
      );
      if (response.data?.transaction) {
        const updated = response.data.transaction as OmheTransaction;
        setAllRows((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
      }
      setPayoutSuggestions(response.data?.suggestions || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject suggested payment');
    } finally {
      setPayoutSuggestionBusyId(null);
    }
  };

  const applyBulkCategory = async () => {
    if (!bulkCategory || selectedIds.length === 0) return;
    setBulkSaving(true);
    setError('');
    try {
      const response = await api.post('/api/personal-finances/omhe/bulk-category', {
        ids: selectedIds,
        proposed_category: bulkCategory,
      });
      const updated = (response.data?.transactions || []) as OmheTransaction[];
      const byId = new Map(updated.map((row) => [row.id, row]));
      setAllRows((prev) =>
        prev.map((row) => {
          const next = byId.get(row.id);
          if (next) return { ...row, ...next };
          if (selectedIds.includes(row.id)) {
            return { ...row, proposed_category: bulkCategory };
          }
          return row;
        })
      );
      setSelectedIds([]);
      setBulkCategory('');
      setEditingId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update selected categories');
    } finally {
      setBulkSaving(false);
    }
  };

  const clearTableSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const tableSearchField = (
    <TextField
      size="small"
      placeholder="Search ID, description or account"
      value={searchInput}
      onChange={(event) => setSearchInput(event.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: searchInput ? (
          <InputAdornment position="end">
            <IconButton aria-label="Clear search" edge="end" size="small" onClick={clearTableSearch}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{ minWidth: 220, width: 280 }}
      data-testid="omhe-table-search"
    />
  );

  return (
    <>
      {error && !isToastOnlyApiErrorMessage(error) ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ md: 'flex-end' }}
        flexWrap="wrap"
        useFlexGap
      >
        <HouseholdFilterField label="Year" minWidth={140} testId="omhe-year-filter">
          <Select
            value={year}
            displayEmpty
            renderValue={(selected) => (selected ? String(selected) : 'All years')}
            onChange={(event) => {
              setPage(1);
              setMonth('');
              onYearChange(String(event.target.value));
            }}
          >
            <MenuItem value="">All years</MenuItem>
            {yearOptions.map((value) => (
              <MenuItem key={value} value={String(value)}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </HouseholdFilterField>
        <HouseholdFilterField label="Account" minWidth={180} testId="omhe-account-filter">
          <Select
            value={account}
            displayEmpty
            renderValue={(selected) => (selected ? String(selected) : 'All accounts')}
            onChange={(event) => {
              setPage(1);
              setAccount(String(event.target.value));
            }}
          >
            <MenuItem value="">All accounts</MenuItem>
            {(stats?.account_totals || []).map((row) => (
              <MenuItem key={row.account} value={row.account}>
                {optionLabel(row.account, row.debit, row.credit, row.count)}
              </MenuItem>
            ))}
          </Select>
        </HouseholdFilterField>
        <HouseholdFilterField label="Type" minWidth={200} testId="omhe-type-filter">
          <Select
            value={transactionType}
            displayEmpty
            renderValue={(selected) => (selected ? String(selected) : 'All types')}
            onChange={(event) => {
              setPage(1);
              setTransactionType(String(event.target.value));
            }}
          >
            <MenuItem value="">All types</MenuItem>
            {(stats?.type_totals || []).map((row) => (
              <MenuItem key={row.transaction_type} value={row.transaction_type}>
                {optionLabel(row.transaction_type, row.debit, row.credit, row.count)}
              </MenuItem>
            ))}
          </Select>
        </HouseholdFilterField>
        <HouseholdFilterField label="Cashflow" minWidth={200} testId="omhe-cashflow-filter">
          <Select
            value={chartSide}
            displayEmpty
            renderValue={(selected) => (selected ? String(selected) : 'All cashflows')}
            onChange={(event) => {
              setPage(1);
              setChartSide(String(event.target.value));
              setChartGroup('');
              setCategory('');
              setSelectedChartNames([]);
            }}
          >
            <MenuItem value="">All cashflows</MenuItem>
            {CASHFLOW_FILTER_OPTIONS.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </HouseholdFilterField>
      </Stack>

      {chartSide || selectedChartNames.length ? (
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={stepChartBack}
            data-testid="omhe-chart-back"
          >
            {`Back to ${chartBackParent}`}
          </Button>
        </Box>
      ) : null}

      {(year || month || account || transactionType || chartSide || chartGroup || category || selectedChartNames.length > 0) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {year ? (
            <HouseholdFilterChip
              label={`Year: ${year}`}
              onDelete={() => {
                setMonth('');
                onYearChange('');
              }}
            />
          ) : null}
          {month ? (
            <HouseholdFilterChip
              label={`Month: ${personalMonthLabel(month) || month}`}
              onDelete={() => {
                setMonth('');
                setPage(1);
              }}
              data-testid="omhe-month-filter-chip"
            />
          ) : null}
          {account ? (
            <HouseholdFilterChip label={`Account: ${account}`} onDelete={() => setAccount('')} />
          ) : null}
          {transactionType ? (
            <HouseholdFilterChip
              label={`Type: ${transactionType}`}
              onDelete={() => setTransactionType('')}
            />
          ) : null}
          {chartSide ? (
            <HouseholdFilterChip
              label={`Cashflow: ${chartSide}`}
              onDelete={() => {
                setChartSide('');
                setChartGroup('');
                setCategory('');
                setSelectedChartNames([]);
                setPage(1);
              }}
              data-testid="omhe-cashflow-filter-chip"
            />
          ) : null}
          {!chartSide
            ? selectedChartNames.map((name) => (
                <HouseholdFilterChip
                  key={`cashflow-${name}`}
                  color="primary"
                  label={`Cashflow: ${name}`}
                  onDelete={() => {
                    setSelectedChartNames((prev) => prev.filter((item) => item !== name));
                    setPage(1);
                  }}
                />
              ))
            : null}
          {chartGroup && chartSide === CHART_EXPENSES && selectedChartNames.length === 0 ? (
            <HouseholdFilterChip
              label={chartGroup}
              onDelete={() => {
                setChartGroup('');
                setCategory('');
                setSelectedChartNames([]);
                setPage(1);
              }}
            />
          ) : null}
          {chartSide === CHART_EXPENSES && !chartGroup
            ? selectedChartNames.map((name) => (
                <HouseholdFilterChip
                  key={`group-${name}`}
                  color="primary"
                  label={name}
                  onDelete={() => {
                    setSelectedChartNames((prev) => prev.filter((item) => item !== name));
                    setPage(1);
                  }}
                />
              ))
            : null}
          {(chartGroup || (chartSide && chartSide !== CHART_EXPENSES)) && selectedChartNames.length
            ? selectedChartNames.map((name) => (
                <HouseholdFilterChip
                  key={`category-${name}`}
                  color="primary"
                  label={
                    isInvestmentPropertyName(name, categoryGroups) ? `Property: ${name}` : name
                  }
                  onDelete={() => {
                    setSelectedChartNames((prev) => prev.filter((item) => item !== name));
                    if (category === name) setCategory('');
                    setPage(1);
                  }}
                />
              ))
            : category && selectedChartNames.length === 0 ? (
            <HouseholdFilterChip
              label={
                isInvestmentPropertyName(category, categoryGroups)
                  ? `Property: ${category}`
                  : category
              }
              onDelete={() => {
                setCategory('');
                setSelectedChartNames([]);
                setPage(1);
              }}
            />
          ) : null}
        </Stack>
      )}

      <Accordion expanded={chartOpen} onChange={(_, open) => setChartOpen(open)} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Income vs expenses</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', py: 6 }}
              data-testid="omhe-chart-loading"
            >
              <CircularProgress />
            </Box>
          ) : (
          <>
            {stackChartByYear ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1 }}
                flexWrap="wrap"
                useFlexGap
                data-testid="omhe-chart-year-legend"
              >
                {chartYearStackKeys.map((stackYear) => (
                  <Chip
                    key={stackYear}
                    size="small"
                    clickable
                    label={stackYear}
                    data-testid={`omhe-chart-year-${stackYear}`}
                    onClick={() => {
                      setPage(1);
                      onYearChange(stackYear);
                    }}
                    sx={{
                      bgcolor: yearLegendFill(stackYear, chartYearAmountTotals),
                      color: yearLegendText(stackYear, chartYearAmountTotals),
                    }}
                  />
                ))}
              </Stack>
            ) : null}
          <Box data-testid="omhe-category-chart">
            <Box
            sx={{ width: '100%', height: Math.max(168, chartRows.length * HOUSEHOLD_CHART_ROW_HEIGHT), cursor: 'pointer' }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartPlotData}
                layout="vertical"
                margin={{ top: 8, right: 148, left: 8, bottom: 8 }}
                style={{ cursor: 'pointer' }}
                onClick={(state: any, event: any) => {
                  const name = householdChartClickName(state, chartPlotData);
                  if (name) handleChartClick(name, event);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={chartAxisEuroTick}
                  domain={chartXAxis.domain}
                  ticks={chartXAxis.ticks}
                  interval={0}
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                />
                {isNettingCashflow(chartSide) ||
                (!chartSide && chartRows.some((row) => row.name === CHART_MORTGAGE_LUMP)) ? (
                  <ReferenceLine x={0} stroke="#94A3B8" />
                ) : null}
                <YAxis
                  type="category"
                  dataKey="name"
                  width={HOUSEHOLD_CHART_Y_AXIS_WIDTH}
                  interval={0}
                  tick={householdChartYAxisTick((name, event) => {
                    if (name) handleChartClick(name, event);
                  }, HOUSEHOLD_CHART_Y_AXIS_WIDTH)}
                />
                <RechartsTooltip
                  cursor={HOUSEHOLD_CHART_HOVER_CURSOR}
                  content={(props) => (
                    <PersonalFinancesChartTooltip
                      active={props.active}
                      payload={props.payload as any}
                      label={props.label}
                      stackByYear={stackChartByYear}
                      years={chartYearStackKeys}
                      colorFor={(rowName, year) => {
                        const row = chartRows.find((item) => item.name === rowName);
                        const base = householdChartRowFill(rowName, chartSide);
                        return year
                          ? rowYearShadeFill(base, year, row?.byYear, chartYearStackKeys)
                          : base;
                      }}
                      formatMoney={(value) => `€${money(value)}`}
                    />
                  )}
                />
                {stackChartByYear
                  ? chartYearStackKeys.map((stackYear, yearIndex) => (
                      <Bar
                        key={stackYear}
                        dataKey={stackYear}
                        name={stackYear}
                        stackId="years"
                        cursor="pointer"
                        isAnimationActive={false}
                        background={yearIndex === 0 ? HOUSEHOLD_CHART_BAR_BACKGROUND : undefined}
                        onClick={(item: any, _index: number, event: any) => {
                          event?.stopPropagation?.();
                          const name = String(item?.payload?.name || '');
                          if (name) handleChartClick(name, event);
                        }}
                      >
                        {chartRows.map((row) => (
                          <Cell
                            key={`${row.name}-${stackYear}`}
                            fill={rowYearShadeFill(
                              householdChartRowFill(row.name, chartSide),
                              stackYear,
                              row.byYear,
                              chartYearStackKeys
                            )}
                            fillOpacity={householdChartBarOpacity(row.name, selectedChartNames)}
                          />
                        ))}
                        {yearIndex === chartYearStackKeys.length - 1 ? (
                          <LabelList
                            dataKey="value"
                            content={householdBarCountLabel(
                              chartRows,
                              (value) => `€${money(value)}`,
                              'omhe-bar-label'
                            )}
                          />
                        ) : null}
                      </Bar>
                    ))
                  : (
                <Bar
                  dataKey="value"
                  cursor="pointer"
                  isAnimationActive={false}
                  background={HOUSEHOLD_CHART_BAR_BACKGROUND}
                  onClick={(item: any, _index: number, event: any) => {
                    event?.stopPropagation?.();
                    const name = String(item?.payload?.name || '');
                    if (name) handleChartClick(name, event);
                  }}
                >
                  {chartRows.map((row) => (
                    <Cell
                      key={row.name}
                      fill={householdChartRowFill(row.name, chartSide)}
                      fillOpacity={householdChartBarOpacity(row.name, selectedChartNames)}
                    />
                  ))}
                  <LabelList
                    dataKey="value"
                    content={householdBarCountLabel(
                      chartRows,
                      (value) => `€${money(value)}`,
                      'omhe-bar-label'
                    )}
                  />
                </Bar>
                  )}
              </BarChart>
            </ResponsiveContainer>
          </Box>
          </Box>
          </>
          )}
        </AccordionDetails>
      </Accordion>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 1 }} data-testid="omhe-pagination-top">
            <PaginationBar
              total={total}
              page={currentPage}
              pages={pages}
              perPage={perPage}
              onPage={setPage}
              onPerPage={(size) => {
                setPerPage(size);
                setPage(1);
              }}
              testId="omhe-page-size-top"
              search={tableSearchField}
            />
          </Box>
          {selectedIds.length > 0 ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1 }}
              data-testid="omhe-bulk-actions"
            >
              <Typography variant="body2">{selectedIds.length} selected</Typography>
              <FormControl size="small" sx={{ minWidth: 220 }} data-testid="omhe-bulk-category">
                <InputLabel>Set category</InputLabel>
                <Select
                  label="Set category"
                  value={bulkCategory}
                  disabled={bulkSaving}
                  onChange={(event) => setBulkCategory(String(event.target.value))}
                >
                  {categoryEditOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="contained"
                disabled={!bulkCategory || bulkSaving}
                onClick={applyBulkCategory}
                data-testid="omhe-bulk-apply"
              >
                {bulkSaving ? 'Updating…' : 'Apply'}
              </Button>
              <Button
                size="small"
                disabled={bulkSaving}
                onClick={() => {
                  setSelectedIds([]);
                  setBulkCategory('');
                }}
              >
                Clear selection
              </Button>
            </Stack>
          ) : null}
          <TableContainer
            component={Paper}
            data-testid="omhe-table-scroll"
            sx={{
              ...FIT_TABLE_CONTAINER_SX,
              maxHeight: 'min(70vh, calc(100vh - 220px))',
              overflowY: 'auto',
              '& .MuiTableCell-stickyHeader': { zIndex: 3, bgcolor: 'background.paper' },
            }}
          >
            <Table size="small" data-testid="omhe-personal-table" stickyHeader sx={FIT_TABLE_SX}>
              <FitColgroup cols={OMHE_TABLE_COLS} />
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" align="center" sx={{ bgcolor: 'background.paper' }}>
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected}
                      onChange={toggleSelectAllOnPage}
                      inputProps={{ 'aria-label': 'Select all OmHe rows' }}
                      data-testid="omhe-select-all"
                    />
                  </TableCell>
                  <OmheSortHeader column="id" label="ID" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="posted_date" label="Date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="cashflow" label="Cashflow" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="category_group" label="Group" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="proposed_category" label="Category" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="allocation" label="Allocation" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="transaction_type" label="Type" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="product" label="Product" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="description" label="Description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="debit_amount" label="Debit" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="credit_amount" label="Credit" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="fee" label="Fee" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="balance" label="Balance" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="card_last4" label="Card" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <OmheSortHeader column="posted_account" label="Account" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={17}>No OmHe transactions match the current filters.</TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      selected={highlightId === row.id || detailRowId === row.id || selectedIds.includes(row.id)}
                      onClick={() => openDetailRow(row.id)}
                      data-testid={`omhe-row-${row.id}`}
                      data-detail-open={detailRowId === row.id ? 'true' : undefined}
                      sx={{
                        cursor: 'pointer',
                        ...(detailRowId === row.id ? openDetailRowSx : {}),
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(row.id)}
                          onChange={() => toggleRowSelected(row.id)}
                          inputProps={{ 'aria-label': `Select OmHe row ${row.id}` }}
                          data-testid={`omhe-row-select-${row.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <FitCellText title={String(row.id)}>{row.id}</FitCellText>
                      </TableCell>
                      <TableCell data-testid={`omhe-date-${row.id}`}>
                        <FitCellText title={formatOmheWhen(row)}>
                          {formatDayMonthYear(row.posted_date)}
                        </FitCellText>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={cashflowOfRow(row, categoryGroups, omhePairedIds, true)} {...fitTableTooltipProps}>
                          <Chip
                            size="small"
                            data-testid={`omhe-cashflow-${row.id}`}
                            sx={{
                              ...fitChipSx,
                              ...cashflowChipSx(cashflowOfRow(row, categoryGroups, omhePairedIds, true)),
                            }}
                            label={displayCashflowLabel(cashflowOfRow(row, categoryGroups, omhePairedIds, true))}
                            color={cashflowColor(cashflowOfRow(row, categoryGroups, omhePairedIds, true))}
                            variant="filled"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell data-testid={`omhe-group-${row.id}`}>
                        <FitCellText title={expenseGroupOfRow(row, categoryGroups, omhePairedIds, true) || '—'}>
                          {expenseGroupOfRow(row, categoryGroups, omhePairedIds, true) || '—'}
                        </FitCellText>
                      </TableCell>
                      <TableCell
                        onClick={
                          editingId === row.id ? (event) => event.stopPropagation() : undefined
                        }
                      >
                        {editingId === row.id ? (
                          <FormControl size="small" fullWidth data-testid="omhe-category-editor">
                            <Select
                              autoFocus
                              value={displayHouseholdCategory(row.proposed_category)}
                              disabled={savingId === row.id}
                              onChange={(event) => saveCategory(row.id, String(event.target.value))}
                            >
                              {categoryEditOptions.map((name) => (
                                <MenuItem key={name} value={name}>
                                  {name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: '100%' }}>
                          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <FitCellText
                            title={row.category_rationale || displayHouseholdCategory(row.proposed_category)}
                            testId={`omhe-category-${row.id}`}
                          >
                            {displayHouseholdCategory(row.proposed_category)}
                          </FitCellText>
                          </Box>
                          {offsetChip(row) ? (
                            <Chip
                              size="small"
                              data-testid={`omhe-offset-chip-${row.id}`}
                              sx={{ ...fitChipSx, flexShrink: 0 }}
                              color={householdOffsetChipColor(offsetChip(row))}
                              variant={householdOffsetChipVariant(offsetChip(row))}
                              label={offsetChip(row)}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (row.offset_of_id) {
                                  openOffsetRow(row.offset_of_id);
                                  return;
                                }
                                const refund = allRows.find((item) => item.offset_of_id === row.id);
                                if (refund) openOffsetRow(refund.id);
                              }}
                            />
                          ) : null}
                        </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <FitCellText title={allocationOfKind(row.expense_kind)}>
                          {allocationOfKind(row.expense_kind)}
                        </FitCellText>
                      </TableCell>
                      <TableCell>
                        <FitCellText title={row.transaction_type || ''}>{row.transaction_type}</FitCellText>
                      </TableCell>
                      <TableCell>
                        <FitCellText title={row.product || ''}>{row.product}</FitCellText>
                      </TableCell>
                      <TableCell>
                        <FitCellText title={row.description || ''}>{row.description}</FitCellText>
                      </TableCell>
                      <TableCell align="right">
                        <FitCellText title={row.debit_amount != null ? money(row.debit_amount) : ''}>
                          {row.debit_amount != null ? money(row.debit_amount) : ''}
                        </FitCellText>
                      </TableCell>
                      <TableCell align="right">
                        <FitCellText title={row.credit_amount != null ? money(row.credit_amount) : ''}>
                          {row.credit_amount != null ? money(row.credit_amount) : ''}
                        </FitCellText>
                      </TableCell>
                      <TableCell align="right">
                        <FitCellText title={row.fee ? money(row.fee) : ''}>
                          {row.fee ? money(row.fee) : ''}
                        </FitCellText>
                      </TableCell>
                      <TableCell align="right">
                        <FitCellText title={row.balance != null ? money(row.balance) : ''}>
                          {row.balance != null ? money(row.balance) : ''}
                        </FitCellText>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.card_last4 || ''} {...fitTableTooltipProps}>
                          <Chip
                            size="small"
                            data-testid={`omhe-card-chip-${row.id}`}
                            sx={{ ...fitChipSx, fontWeight: 500 }}
                            label={displayCardLast4(row.card_last4)}
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.posted_account || ''} {...fitTableTooltipProps}>
                          <Chip
                            size="small"
                            data-testid={`omhe-account-chip-${row.id}`}
                            sx={{ ...fitChipSx, fontWeight: 500, ...accountTagChipSx(row.posted_account) }}
                            label={row.posted_account || '—'}
                            variant="filled"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Stack direction="row" spacing={0} justifyContent="flex-end">
                          <Tooltip
                            title={
                              detailRowId === row.id
                                ? 'Details open in right panel'
                                : 'Open details in right panel'
                            }
                            {...fitTableTooltipProps}
                          >
                            <IconButton
                              size="small"
                              aria-label={`Open details for ${row.id}`}
                              data-testid={`omhe-details-${row.id}`}
                              color={detailRowId === row.id ? 'primary' : 'default'}
                              onClick={() => {
                                setCommentFocus(false);
                                setDetailRowId((current) => (current === row.id ? null : row.id));
                              }}
                            >
                              <ViewSidebarIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <HouseholdTxCommentButton
                            rowId={row.id}
                            comment={row.user_comment}
                            comments={row.comments}
                            testIdPrefix="omhe"
                            onClick={() => openDetailRow(row.id, { focusComment: true })}
                          />
                          <Tooltip title="View similar transactions" {...fitTableTooltipProps}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`View similar transactions for ${row.id}`}
                                data-testid={`omhe-similar-${row.id}`}
                                disabled={!similarSearchNeedle(row.description)}
                                onClick={() => setSimilarRowId(row.id)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit category" {...fitTableTooltipProps}>
                            <IconButton
                              size="small"
                              aria-label={`Edit category for ${row.id}`}
                              data-testid={`omhe-edit-${row.id}`}
                              onClick={() =>
                                setEditingId((current) => (current === row.id ? null : row.id))
                              }
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 1 }} data-testid="omhe-pagination-bottom">
            <PaginationBar
              total={total}
              page={currentPage}
              pages={pages}
              perPage={perPage}
              onPage={setPage}
              onPerPage={(size) => {
                setPerPage(size);
                setPage(1);
              }}
              testId="omhe-page-size-bottom"
            />
          </Box>
        </>
      )}
      <Drawer
        anchor="right"
        open={Boolean(detailRow)}
        onClose={closeDetailRow}
        hideBackdrop
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          sx: {
            zIndex: (theme) => theme.zIndex.snackbar + 15,
            pointerEvents: 'none',
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: DETAIL_PANEL_WIDTH_PX },
            maxWidth: '100vw',
            zIndex: 1,
            pointerEvents: 'auto',
            borderLeft: 1,
            borderColor: 'divider',
            boxShadow: 12,
          },
        }}
      >
        {detailRow ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="omhe-detail-panel">
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.5,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                  <Typography variant="h6" component="div" sx={{ lineHeight: 1.25 }}>
                    TX {detailRow.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {formatOmheWhen(detailRow)} · {detailRow.posted_account || '—'} ·{' '}
                    {detailRow.debit_amount
                      ? money(detailRow.debit_amount)
                      : money(detailRow.credit_amount)}
                  </Typography>
                </Box>
                <Tooltip title="Close panel">
                  <IconButton
                    aria-label="Close transaction detail panel"
                    edge="end"
                    onClick={closeDetailRow}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {detailCounterpart ? (
                <>
                  <OmheOffsetSideCard
                    row={detailRow}
                    role="this"
                    offsetLabel={offsetChip(detailRow)}
                  />
                  <OmheOffsetSideCard
                    row={detailCounterpart}
                    role="other"
                    offsetLabel={offsetChip(detailCounterpart)}
                    onOpen={() => openDetailRow(detailCounterpart.id)}
                  />
                </>
              ) : offsetChip(detailRow) ? (
                <DetailLine label="Offset" value={offsetChip(detailRow)} />
              ) : null}
              {isHealthInsurancePayout(detailRow) && !detailRow.offset_of_id ? (
                <Box sx={{ mb: 2 }} data-testid="omhe-payout-suggestions">
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Suggested claim payments
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Laya usually reimburses about 50% of the original medical payment. Confirm or reject
                    each match — nothing is linked automatically.
                  </Typography>
                  {payoutSuggestionsLoading ? (
                    <CircularProgress size={20} />
                  ) : payoutSuggestions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No ~50% matches in the previous 180 days.
                    </Typography>
                  ) : (
                    payoutSuggestions.map((item) => (
                      <Paper
                        key={item.id}
                        variant="outlined"
                        data-testid={`omhe-payout-suggest-${item.id}`}
                        sx={{ p: 1.25, mb: 1 }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2">
                              TX {item.id} · {item.posted_date ? formatDayMonthYear(item.posted_date) : '—'} ·{' '}
                              {item.posted_account || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {displayHouseholdCategory(item.proposed_category)} · {item.description || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {money(item.debit_amount)} · ~{Math.round(item.ratio * 100)}% reimbursed ·{' '}
                              {item.days_before === 0
                                ? 'same day'
                                : `${item.days_before} day${item.days_before === 1 ? '' : 's'} earlier`}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<CheckIcon fontSize="small" />}
                              disabled={payoutSuggestionBusyId != null}
                              data-testid={`omhe-payout-suggest-confirm-${item.id}`}
                              onClick={() => confirmPayoutSuggestion(item.id)}
                            >
                              Confirm
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              startIcon={<CloseIcon fontSize="small" />}
                              disabled={payoutSuggestionBusyId != null}
                              data-testid={`omhe-payout-suggest-reject-${item.id}`}
                              onClick={() => rejectPayoutSuggestion(item.id)}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))
                  )}
                </Box>
              ) : null}
              <DetailLine label="ID" value={detailRow.id} />
              <DetailLine label="Date" value={formatOmheWhen(detailRow)} />
              <DetailLine label="Account" value={detailRow.posted_account} />
              <DetailLine label="Card" value={displayCardLast4(detailRow.card_last4)} />
              <DetailLine label="Type" value={detailRow.transaction_type} />
              <DetailLine label="Product" value={detailRow.product} />
              <DetailLine
                label="Cashflow"
                value={cashflowOfRow(detailRow, categoryGroups, omhePairedIds, true)}
              />
              <DetailLine
                label="Group"
                value={expenseGroupOfRow(detailRow, categoryGroups, omhePairedIds, true) || '—'}
              />
              <DetailLine
                label="Category"
                value={displayHouseholdCategory(detailRow.proposed_category)}
              />
              <DetailLine
                label="Allocation"
                value={allocationOfKind(detailRow.expense_kind)}
              />
              <DetailLine label="Description" value={detailRow.description} />
              <DetailLine label="Debit" value={detailRow.debit_amount != null ? money(detailRow.debit_amount) : '—'} />
              <DetailLine label="Credit" value={detailRow.credit_amount != null ? money(detailRow.credit_amount) : '—'} />
              <DetailLine label="Fee" value={detailRow.fee ? money(detailRow.fee) : '—'} />
              <DetailLine label="Balance" value={detailRow.balance != null ? money(detailRow.balance) : '—'} />
              <DetailLine label="Currency" value={detailRow.posted_currency} />
              <DetailLine label="State" value={detailRow.state} />
              <DetailLine label="Rationale" value={detailRow.category_rationale} />
              <HouseholdTxCommentThread
                household="omhe"
                rowId={detailRow.id}
                comments={detailRow.comments}
                testIdPrefix="omhe"
                autoFocus={commentFocus}
                onTransaction={(patch) => applyOmheTransaction({ ...detailRow, ...patch })}
                onError={setError}
              />
            </Box>
          </Box>
        ) : null}
      </Drawer>
      {similarAnchor ? (
        <HouseholdSimilarTxDialog
          open
          needle={similarSearchNeedle(similarAnchor.description)}
          rows={similarTxViews}
          onClose={() => setSimilarRowId(null)}
          onSelectRow={(id) => {
            setSimilarRowId(null);
            openDetailRow(id);
          }}
        />
      ) : null}
    </>
  );
};

export default PersonalFinancesOmhe;
